import { useEffect, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { Mail, CheckCircle2, X, ArrowRight } from 'lucide-react';
import { api } from '@/client/lib/api';
import { Button } from '@/client/components/ui/button';
import { isLocale, type Locale } from '@/client/i18n';
import type { Me } from '@/client/hooks/useMe';

type Status = 'pending' | 'success' | 'failed';

export function VerifyEmail() {
  const { t } = useTranslation();
  const params = useParams();
  const locale: Locale = isLocale(params.locale) ? params.locale : 'sk';
  const [search] = useSearchParams();
  const token = search.get('token');
  const queryClient = useQueryClient();
  const [status, setStatus] = useState<Status>('pending');

  useEffect(() => {
    if (!token) {
      setStatus('failed');
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        const data = await api<{ user: Me }>('/api/v1/auth/email/verify', {
          method: 'POST',
          body: { token },
        });
        if (cancelled) return;
        queryClient.setQueryData(['me'], data.user);
        setStatus('success');
      } catch {
        if (cancelled) return;
        setStatus('failed');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token, queryClient]);

  return (
    <div className="relative">
      <div className="mx-auto max-w-md px-5 sm:px-6 py-12 sm:py-20">
        <div className="dr-card-strong p-8 sm:p-10 text-center animate-fade-up">
          {status === 'pending' && (
            <>
              <div className="relative mx-auto h-20 w-20 mb-6">
                <div
                  className="absolute inset-0 rounded-full"
                  style={{
                    background:
                      'conic-gradient(from 220deg, rgba(193,167,226,0.40), rgba(117,59,189,0.18), rgba(193,167,226,0.40))',
                    filter: 'blur(8px)',
                  }}
                  aria-hidden
                />
                <div className="relative h-full w-full rounded-2xl bg-gradient-to-br from-primary/20 to-primary/5 border border-primary/15 flex items-center justify-center animate-float">
                  <Mail size={34} className="text-primary" />
                </div>
              </div>
              <p className="dr-eyebrow">
                {t('auth.verify_pending_eyebrow', { defaultValue: 'Overujem' })}
              </p>
              <h1 className="mt-3 font-display text-h2 text-primary-dark">
                {t('auth.verify_title')}
              </h1>
              <div className="mt-4 inline-flex items-center gap-3 font-sans text-body text-text-secondary">
                <span className="h-2 w-2 rounded-full bg-primary animate-pulse" />
                {t('auth.verify_pending')}
              </div>
            </>
          )}

          {status === 'success' && (
            <>
              <div className="relative mx-auto h-20 w-20 mb-6">
                <div
                  className="absolute inset-0 rounded-full"
                  style={{
                    background:
                      'conic-gradient(from 220deg, rgba(45,139,85,0.30), rgba(193,167,226,0.30), rgba(45,139,85,0.30))',
                    filter: 'blur(8px)',
                  }}
                  aria-hidden
                />
                <div className="relative h-full w-full rounded-2xl bg-gradient-to-br from-primary/20 to-primary/5 border border-primary/15 flex items-center justify-center animate-float">
                  <CheckCircle2 size={36} className="text-success" />
                </div>
              </div>
              <div className="inline-flex items-center gap-2 rounded-pill bg-success/10 border border-success/25 text-success px-3 py-1 font-sans text-caption font-medium">
                <CheckCircle2 size={12} />
                {locale === 'cs' ? 'Ověřeno' : 'Overené'}
              </div>
              <h1 className="mt-4 font-display text-h2 text-primary-dark">
                {t('auth.verify_title')}
              </h1>
              <p className="mt-3 font-sans text-body text-text-secondary leading-relaxed">
                {t('auth.verify_success')}
              </p>
              <Link to={`/${locale}/portal`} className="inline-block mt-8">
                <Button size="lg">
                  {t('nav.portal')}
                  <ArrowRight size={16} />
                </Button>
              </Link>
            </>
          )}

          {status === 'failed' && (
            <>
              <div className="mx-auto h-20 w-20 mb-6 rounded-2xl bg-gradient-to-br from-error/15 to-error/5 border border-error/20 flex items-center justify-center">
                <X size={36} className="text-error" />
              </div>
              <div className="inline-flex items-center gap-2 rounded-pill bg-error/10 border border-error/25 text-error px-3 py-1 font-sans text-caption font-medium">
                <X size={12} />
                {locale === 'cs' ? 'Chyba' : 'Chyba'}
              </div>
              <h1 className="mt-4 font-display text-h2 text-primary-dark">
                {t('auth.verify_title')}
              </h1>
              <p className="mt-3 font-sans text-body text-text-secondary leading-relaxed">
                {t('auth.verify_failed')}
              </p>
              <Link to={`/${locale}/prihlasenie`} className="inline-block mt-8">
                <Button variant="secondary" size="lg">
                  {t('nav.login')}
                </Button>
              </Link>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
